import { memo, useState, useLayoutEffect } from 'react';
import { Progress } from 'antd';
import classNames from 'classnames';
import { sleep } from 'utils/sleepUtil';
import './style.scss';
import { GUEST_RESERVE_SERVICE_STEP } from 'constants/reservation';

/**
 * ゲスト予約の予約画面上部のステップ表示部品
 */
export default memo(
  ({
    previousStep = -1,
    activeStep = 0,
    bottomElement = null,
    className = { stepperClass: '', bottomClass: '' },
    stepPaths = [],
    navigate = null,
    locationState = null,
    enableLabelUnderline = true,
  }) => {
    const [_activeStep, setActiveStep] = useState(previousStep);
    const [_percent, setPercent] = useState(null);
    const [_init, setInit] = useState(true);
    const [isBottomDisplay, setBottomDisplay] = useState(false);

    useLayoutEffect(() => {
      (async () => {
        setBottomDisplay(false);
        if (_init) {
          // 初回表示時
          await sleep(0.5);
          if (previousStep > activeStep) {
            await backStep(activeStep);
            await displayBottomElement();
          } else {
            await nextStep(activeStep);
          }
          setInit(false);
        } else if (previousStep === _activeStep) {
          // 一つ進む
          await nextStep(activeStep);
        } else {
          // 戻る（1 or 2）
          await backStep(activeStep + 1);
          await sleep(0.2);
          await backStep(activeStep);
          setActiveStep(activeStep);
          await displayBottomElement();
        }
      })();
    }, [activeStep]);

    // ステップを一つ進める
    const nextStep = async step => {
      setPercent(calcPercent(step));
      await sleep(0.5);
      setActiveStep(step);
      await displayBottomElement();
    };

    // ステップを一つ戻す
    const backStep = async step => {
      setActiveStep(step);
      await sleep(0.1);
      setPercent(calcPercent(step));
    };

    // 下部ビューを表示する
    const displayBottomElement = async () => {
      await sleep(0.2);
      setBottomDisplay(true);
    };

    // プログレスバーの値を計算
    const calcPercent = step => {
      const stepsLength = GUEST_RESERVE_SERVICE_STEP.length;
      if (step === -1 || step === 0) return 0;
      if (step === stepsLength - 1) return 100;
      return (step / (stepsLength - 1)) * 100;
    };

    return (
      <>
        <div style={{ textAlign: '-webkit-center', margin: '0 auto' }}>
          <div className="w-full sm:min-w-[600px]">
            <div
              className={classNames(
                'stepper relative h-auto w-auto text-left',
                className.stepperClass
              )}
            >
              <Progress
                className="accent-red"
                percent={
                  _percent !== null ? _percent : calcPercent(previousStep)
                }
                showInfo={false}
                strokeColor={'#CA4539'}
                trailColor={'#D0D0D9'}
              />
              {GUEST_RESERVE_SERVICE_STEP.map((label, index) => {
                const stepsLength = GUEST_RESERVE_SERVICE_STEP.length;
                const position =
                  stepsLength === 1
                    ? '50%'
                    : index === 0
                    ? '0%'
                    : index === stepsLength - 1
                    ? '100%'
                    : `${(index / (stepsLength - 1)) * 100}%`;

                const stepperClassName = classNames(
                  'absolute top-0 flex w-[30px] flex-col bg-background-main'
                );
                const stepperStyle = {
                  left: `calc(${position} - 15px)`,
                };

                return (
                  <span
                    key={label}
                    className={stepperClassName}
                    style={stepperStyle}
                  >
                    <span
                      className={classNames(
                        'relative h-[30px] w-[30px] items-center rounded-full transition-all',
                        index <= _activeStep ? 'bg-red/40' : 'bg-line/40'
                      )}
                    >
                      <span
                        className={classNames(
                          'absolute left-[5px] top-[5px] h-5 w-5 rounded-full transition-all',
                          index <= _activeStep ? 'bg-red' : 'bg-line'
                        )}
                      />
                    </span>
                    <label
                      onClick={() => {
                        if (
                          enableLabelUnderline &&
                          index < _activeStep &&
                          stepPaths?.[index] &&
                          navigate
                        ) {
                          navigate(stepPaths[index], { state: locationState });
                        }
                      }}
                      className={classNames(
                        'self-center whitespace-nowrap text-[12px] transition-all sm:text-[18px]',
                        index <= _activeStep ? 'text-red' : 'text-line',
                        enableLabelUnderline && index < _activeStep ? 'cursor-pointer underline' : ''
                      )}
                    >
                      {label}
                    </label>
                  </span>
                );
              })}
            </div>
          </div>
          <div
            className={classNames('mt-14 h-auto', className.bottomClass)}
            style={{
              margin: '0 auto',
            }}
          >
            {bottomElement ? (
              <div
                className={classNames(
                  'transition-all duration-300',
                  isBottomDisplay ? 'opacity-100' : 'opacity-0'
                )}
                style={{
                  textAlign: '-webkit-center',
                }}
              >
                {bottomElement}
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </>
    );
  }
);
